import React from 'react';
import {
    Button,
    Spinner,
    Alert,
    FormGroup,
    Label,
    Row,
    Col,
    Card,
    CardBody,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileImport,
    faCheckCircle,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    FlexCenterRow,
    SubHeading,
    Input,
    FormCheckbox,
} from '../common/forms/FormElements';
import SlideForm from '../common/forms/SlideForm';
import CommonContext, { ApiRoutes } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';

export default class EmployeeImportSlideout extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        this.state = {
            loading: false,
            externalEmployees: [],
            companies: [],
            dispatchCompanies: [],
            groups: [],
            roadsEmployee: [],
            hasError: false,
            errorMessage: '',
            masterPassword: '',
            validationError: '',
            isSubmitting: false,
        };
    }

    open = async (externalEmployees = []) => {
        this.setState({
            loading: true,
            externalEmployees: [],
            hasError: false,
            errorMessage: '',
            masterPassword: '',
            validationError: '',
        });

        try {
            const employees = (!externalEmployees || externalEmployees.length === 0)
                ? await httpClient.get(ApiRoutes.externalEmployee.all())
                : externalEmployees;

            const [companies, dispatchCompanies, groups, roadsEmployee] = await Promise.all([
                httpClient.js(ApiRoutes.company.payrollCompanies()),
                httpClient.js(ApiRoutes.company.dispatchCompanies()),
                httpClient.js(ApiRoutes.auth.groups()),
                httpClient.js(ApiRoutes.typeAheads.newHires()),
            ]);

            const employeesWithFormData = (employees || []).map((emp) => ({
                ...emp,
                forImport: emp.forImport || false,
                selectedGroups: [],
                password: '',
            }));

            this.setState({
                loading: false,
                externalEmployees: employeesWithFormData,
                companies,
                dispatchCompanies,
                groups,
                roadsEmployee,
                hasError: false,
            });
        } catch (error) {
            const errorMessage = error?.handledError?.message
                || error?.message
                || 'Error loading external employees';
            this.setState({
                loading: false,
                hasError: true,
                errorMessage,
            });
        }
    };

    onClose = () => {
        this.setState({
            loading: false,
            externalEmployees: [],
            companies: [],
            dispatchCompanies: [],
            groups: [],
            roadsEmployee: [],
            hasError: false,
            errorMessage: '',
        });
        this.props.toggleShow(false);
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    onForImportChange = (empId) => {
        this.setState((prevState) => ({
            externalEmployees: prevState.externalEmployees.map((emp) => (emp.id === empId
                ? { ...emp, forImport: !emp.forImport }
                : emp)),
        }));
    };

    onDispatchCompanyChange = (empId, value) => {
        this.setState((prevState) => ({
            externalEmployees: prevState.externalEmployees.map((emp) => (emp.id === empId
                ? { ...emp, dispatchCompanyId: value?.id || null }
                : emp)),
        }));
    };

    onGroupsChange = (empId, value) => {
        this.setState((prevState) => ({
            externalEmployees: prevState.externalEmployees.map((emp) => (emp.id === empId
                ? { ...emp, selectedGroups: value || [] }
                : emp)),
        }));
    };

    onPasswordChange = (empId, event) => {
        const { value } = event.target;
        this.setState((prevState) => ({
            externalEmployees: prevState.externalEmployees.map((emp) => (emp.id === empId
                ? { ...emp, password: value }
                : emp)),
        }));
    };

    onLinktoChange = (empId, value) => {
        this.setState((prevState) => ({
            externalEmployees: prevState.externalEmployees.map((emp) => (emp.id === empId
                ? { ...emp, linkTo: value?.value || null }
                : emp)),
        }));
    };

    onMasterPasswordChange = (event) => {
        this.setState({ masterPassword: event.target.value });
    };

    applyMasterPassword = () => {
        const { masterPassword } = this.state;
        if (!masterPassword) {
            return;
        }

        this.setState((prevState) => ({
            externalEmployees: prevState.externalEmployees.map((emp) => ({
                ...emp,
                password: masterPassword,
            })),
        }));
    };

    validateEmployees = () => {
        const { externalEmployees, companies } = this.state;
        const employeesToImport = externalEmployees.filter((emp) => emp.forImport);

        if (employeesToImport.length === 0) {
            return 'Please select at least one employee to import.';
        }

        const invalidEmployees = employeesToImport.filter(
            (emp) => !emp.linkTo &&
                (!emp.dispatchCompanyId
                    || !emp.selectedGroups
                    || emp.selectedGroups.length === 0
                    || !emp.password),
        );

        if (invalidEmployees.length > 0) {
            const maxNamesToShow = 5;
            const names = invalidEmployees.slice(0, maxNamesToShow).map((emp) => {
                const company = companies.find((c) => c.id === emp.companyId);
                return `${emp.firstName} ${emp.lastName}${company ? ` (${company.companyName})` : ''}`;
            });

            const additional = invalidEmployees.length > maxNamesToShow
                ? ` and ${invalidEmployees.length - maxNamesToShow} more...`
                : '';

            return `The following employees are missing required fields: ${names.join(', ')}${additional}`;
        }

        return null;
    };

    onSubmit = async () => {
        const validationError = this.validateEmployees();

        if (validationError) {
            this.setState({ validationError });
            return;
        }

        this.setState({
            validationError: '',
            isSubmitting: true,
            hasError: false,
            errorMessage: '',
        });

        try {
            const { externalEmployees } = this.state;
            const employeesToImport = externalEmployees.filter((emp) => emp.forImport);
            const employeesPayload = employeesToImport.map((emp) => ({
                ...emp,
                groupIds: emp.selectedGroups.map((g) => g.id),
            }));

            const result = await httpClient.post(ApiRoutes.employeeImport.import(), employeesPayload);

            if (result?.message && result.message.includes('Errors:')) {
                this.setState({
                    isSubmitting: false,
                    hasError: true,
                    errorMessage: result.message,
                });
                return;
            }

            this.setState({ isSubmitting: false });
            this.onClose();

            if (this.props.onImportSuccess) {
                this.props.onImportSuccess();
            }
        } catch (error) {
            const errorMessage = error?.handledError?.message
                || error?.message
                || 'Error importing employees';
            this.setState({
                isSubmitting: false,
                hasError: true,
                errorMessage,
            });
        }
    };

    render() {
        const {
            loading,
            externalEmployees,
            companies,
            dispatchCompanies,
            groups,
            roadsEmployee,
            masterPassword,
            hasError,
            errorMessage,
            validationError,
            isSubmitting,
        } = this.state;

        return (
            <SlideForm
                size="col-12"
                loading={false}
                show={this.props.show}
                id="employeeImportForm"
                formIcon={faFileImport}
                formTitle="Import Employees from ADP"
                ref={this.formRef}
                fullScreen={false}
                className="pb-2 w-100"
                onClose={this.onClose}
                hideToolbar
            >
                <SubHeading>External Employees</SubHeading>

                {loading && (
                    <div className="text-center py-5">
                        <Spinner
                            size="lg"
                            color="primary"
                        />
                        <div className="mt-3">
                            <strong>Loading employee data...</strong>
                        </div>
                        <div className="text-muted mt-2">This may take a few moments</div>
                    </div>
                )}

                {!loading && hasError && (
                    <Alert
                        color="danger"
                        className="d-flex align-items-center"
                    >
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="mr-2"
                            size="lg"
                        />
                        <div>
                            <strong>Error</strong>
                            <div className="mt-1">{errorMessage}</div>
                        </div>
                    </Alert>
                )}

                {!loading && (!externalEmployees || externalEmployees.length === 0) && (
                    <Alert color="info">
                        No new employees to import.
                    </Alert>
                )}

                {!loading && validationError && (
                    <Alert
                        color="danger"
                        className="d-flex align-items-center"
                    >
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="mr-2"
                            size="lg"
                        />
                        <div>
                            <strong>Validation Error</strong>
                            <div className="mt-1">{validationError}</div>
                        </div>
                    </Alert>
                )}

                {!loading && externalEmployees && externalEmployees.length > 0 && (
                    <div>
                        <Alert
                            color="success"
                            className="d-flex align-items-center mb-3"
                        >
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="mr-2"
                            />
                            <div>
                                <div className="mt-1">
                                    Found
                                    {' '}
                                    <strong>{externalEmployees.length}</strong>
                                    {' '}
                                    employee
                                    {externalEmployees.length !== 1 ? 's' : ''}
                                    {' '}
                                    ready to import
                                </div>
                            </div>
                        </Alert>

                        <Card className="mb-3 bg-light">
                            <CardBody>
                                <Row className="align-items-center">
                                    <Col md="9">
                                        <FormGroup className="mb-0">
                                            <Label className="small mb-1">
                                                <strong>Set Password for All Employees</strong>
                                            </Label>
                                            <Input
                                                type="password"
                                                value={masterPassword}
                                                onChange={this.onMasterPasswordChange}
                                                placeholder="Enter password to apply to all employees..."
                                                autoComplete="new-password"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col
                                        md="3"
                                        className="d-flex align-items-end"
                                    >
                                        <Button
                                            color="primary"
                                            size="sm"
                                            onClick={this.applyMasterPassword}
                                            disabled={!masterPassword}
                                        >
                                            Apply to All
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {externalEmployees.map((emp) => {
                                const selectedDispatch = dispatchCompanies.find(
                                    (d) => d.id === emp.dispatchCompanyId,
                                );
                                const linkTo = roadsEmployee.find(
                                    (d) => d.value === emp.linkTo,
                                );

                                const company = companies.find((c) => c.id === emp.companyId);
                                const companyName = company?.companyName || '-';
                                const officeName = selectedDispatch?.companyName || '-';

                                return (
                                    <Card
                                        key={emp.id}
                                        className="mb-3"
                                    >
                                        <CardBody>
                                            <Row>
                                                <Col
                                                    xs="auto"
                                                    style={{ width: '50px' }}
                                                    className="d-flex align-items-center"
                                                >
                                                    <FormCheckbox
                                                        id={`forImport-${emp.id}`}
                                                        checked={emp.forImport}
                                                        onChange={() => this.onForImportChange(emp.id)}
                                                    />
                                                </Col>

                                                <Col>
                                                    <Row className="mb-2">
                                                        <Col>
                                                            <div>
                                                                <strong>
                                                                    {emp.firstName} {emp.middleInitial && `${emp.middleInitial}. `}{emp.lastName}
                                                                </strong>
                                                            </div>
                                                            <div className="text-muted small">
                                                                Associate ID:
                                                                {' '}
                                                                <strong>{emp.externalId || '-'}</strong>
                                                                {' | '}
                                                                Payroll File #:
                                                                {' '}
                                                                <strong>{emp.payrollFileNumber || '-'}</strong>
                                                                {' | '}
                                                                Company:
                                                                {' '}
                                                                <strong>{companyName}</strong>
                                                                {' | '}
                                                                Dispatch Office:
                                                                {' '}
                                                                <strong>{officeName}</strong>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col md="3">
                                                            <FormGroup className="mb-0">
                                                                <Label className="small mb-1">
                                                                    Dispatch Location
                                                                    {' '}
                                                                    <span className="text-danger">*</span>
                                                                </Label>
                                                                <ValidatedSelect
                                                                    options={dispatchCompanies}
                                                                    value={selectedDispatch || null}
                                                                    getOptionValue={(option) => option.id}
                                                                    getOptionLabel={(option) => option.companyName}
                                                                    onChange={(value) => this.onDispatchCompanyChange(emp.id, value)}
                                                                    placeholder="Select Dispatch Location..."
                                                                    isDisabled={!emp.forImport || !!emp.linkTo}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md="3">
                                                            <FormGroup className="mb-0">
                                                                <Label className="small mb-1">
                                                                    Groups
                                                                    {' '}
                                                                    <span className="text-danger">*</span>
                                                                </Label>
                                                                <ValidatedSelect
                                                                    options={groups}
                                                                    value={emp.selectedGroups || []}
                                                                    getOptionValue={(option) => option.id}
                                                                    getOptionLabel={(option) => option.groupName}
                                                                    onChange={(value) => this.onGroupsChange(emp.id, value)}
                                                                    isMulti
                                                                    placeholder="Select Groups..."
                                                                    isDisabled={!emp.forImport || !!emp.linkTo}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md="3">
                                                            <FormGroup className="mb-0">
                                                                <Label className="small mb-1">
                                                                    Password
                                                                    {' '}
                                                                    <span className="text-danger">*</span>
                                                                </Label>
                                                                <Input
                                                                    type="password"
                                                                    id={`password-${emp.id}`}
                                                                    value={emp.password}
                                                                    onChange={(e) => this.onPasswordChange(emp.id, e)}
                                                                    placeholder="Enter password..."
                                                                    disabled={!emp.forImport || !!emp.linkTo}
                                                                    autoComplete="new-password"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md="3">
                                                            <FormGroup className="mb-0">
                                                                <Label className="small mb-1">
                                                                    Link to
                                                                </Label>
                                                                <ValidatedSelect
                                                                    options={roadsEmployee || []}
                                                                    value={linkTo || null}
                                                                    onChange={(value) => this.onLinktoChange(emp.id, value)}
                                                                    placeholder="Select an Employee..."
                                                                    isDisabled={!emp.forImport}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                )}

                <FlexCenterRow className="pt-4">
                    <Button
                        type="button"
                        color="secondary"
                        onClick={this.onClose}
                        disabled={loading || isSubmitting}
                        className="mr-2"
                    >
                        Close
                    </Button>
                    {externalEmployees && externalEmployees.length > 0 && (
                        <Button
                            type="button"
                            color="primary"
                            onClick={this.onSubmit}
                            disabled={loading || isSubmitting}
                        >
                            {isSubmitting ? 'Importing...' : 'Import Selected Employees'}
                        </Button>
                    )}
                </FlexCenterRow>
            </SlideForm>
        );
    }
}
